import { labelClass, selectField } from '../governed-mcp-gateway/components/GatewayUi';
import { formatQuotaMonth } from '../governed-mcp-gateway/quotaUsage';
import { logOmniChat } from './omniChatLogger';
import type { OmniChatFormState, OmniChatGateway, OmniChatInferenceKey } from './omniChatTypes';

function keyLabel(item: OmniChatInferenceKey): string {
  const last4 = item.last4 ? `••••${item.last4}` : 'issued';
  if (!item.month) return `${item.clientName || 'Gateway key'} · ${last4}`;
  return `${item.clientName} · ${last4} · ${formatQuotaMonth(item.month)}`;
}

export default function OmniChatGatewayFields({
  form,
  gateways,
  inferenceKeys,
  isLight,
  onPatch,
}: {
  form: OmniChatFormState;
  gateways: OmniChatGateway[];
  inferenceKeys: OmniChatInferenceKey[];
  isLight: boolean;
  onPatch: (next: Partial<OmniChatFormState>) => void;
}) {
  const gatewayMissing = Boolean(form.gatewayId && !gateways.some((item) => item.id === form.gatewayId));
  const issued = inferenceKeys.filter((item) => item.gatewayId === form.gatewayId);
  const keyMissing = Boolean(form.inferenceQuotaId && !issued.some((item) => item.id === form.inferenceQuotaId));
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';

  return (
    <div className="space-y-4">
      <label className="block">
        <span className={labelClass(isLight)}>LLM Gateway</span>
        <select
          className={`${selectField(isLight)} w-full`}
          value={form.gatewayId}
          onChange={(event) => {
            const gatewayId = event.target.value;
            const nextKey = inferenceKeys.find((item) => item.gatewayId === gatewayId && item.id === gatewayId);
            onPatch({
              gatewayId,
              inferenceQuotaId:
                gatewayId === form.gatewayId ? form.inferenceQuotaId : nextKey?.id || '',
            });
            logOmniChat('app', 'gateway_selected', { gatewayId });
          }}
        >
          {!gateways.length && !form.gatewayId ? <option value="">No gateways</option> : null}
          {gatewayMissing ? <option value={form.gatewayId}>{form.gatewayId} (unavailable)</option> : null}
          {gateways.map((gateway) => (
            <option key={gateway.id} value={gateway.id}>
              {gateway.name} · {gateway.model}
            </option>
          ))}
        </select>
      </label>
      {form.gatewayId ? (
        <label className="block">
          <span className={labelClass(isLight)}>LLM Gateway key</span>
          <select
            className={`${selectField(isLight)} w-full`}
            value={form.inferenceQuotaId}
            onChange={(event) => {
              onPatch({ inferenceQuotaId: event.target.value });
              logOmniChat('app', 'inference_key_selected', { quotaId: event.target.value, gatewayId: form.gatewayId });
            }}
          >
            <option value="">{issued.length ? 'Select a key' : 'No issued keys'}</option>
            {keyMissing ? <option value={form.inferenceQuotaId}>{form.inferenceQuotaId} (unavailable)</option> : null}
            {issued.map((item) => (
              <option key={item.id} value={item.id}>
                {keyLabel(item)}
              </option>
            ))}
          </select>
          {!issued.length ? (
            <p className={`mt-1.5 text-[12px] ${muted}`}>Issue a key for this gateway in API Keys first.</p>
          ) : null}
        </label>
      ) : null}
    </div>
  );
}
